import { X } from '@phosphor-icons/react';
import classNames from 'classnames';
import { type CSSProperties, useEffect } from 'react';

import { webThemeToCSSVariables } from '@/postContentElement/utils';

import SimpleModal from '../ui/SimpleModal';
import { modalContentContainer, modalDialogContent } from './RecommendationsModal.css';
import type { ProprietaryRecommendationsModalElementProps } from './RecommendationsModal.type';
import { RecommendationRow } from './components/RecommendationRow';
import { SubscribeAllButton } from './components/SubscribeAllButton';

// Skeleton loading component that matches RecommendationRow dimensions
const RecommendationRowSkeleton = () => {
  return (
    <div className="p-4 rounded-[--site-theme-border-radius] border border-[--site-theme-border-color] bg-[--site-theme-background-color] flex flex-col gap-2 opacity-20">
      <div className="flex flex-col gap-3">
        {/* Logo and Subscribe Button Row */}
        <div className="flex justify-between items-start">
          <div className="w-[56px] h-[56px] rounded bg-[--site-theme-border-color] animate-pulse" />
          <div className="px-4 py-2 rounded-[--site-theme-border-radius] bg-[--site-theme-border-color] animate-pulse w-20 h-8" />
        </div>

        {/* Text Content */}
        <div className="flex flex-col gap-1">
          <div className="h-4 bg-[--site-theme-border-color] animate-pulse rounded w-32" />
          <div className="h-5 bg-[--site-theme-border-color] animate-pulse rounded w-48" />
          <div className="h-4 bg-[--site-theme-border-color] animate-pulse rounded w-full" />
          <div className="h-4 bg-[--site-theme-border-color] animate-pulse rounded w-3/4" />
        </div>
      </div>
    </div>
  );
};

export function ProprietaryRecommendationsModalElement(props: ProprietaryRecommendationsModalElementProps) {
  const {
    email,
    theme,
    isLoading = true,
    isOpen = false,
    recommendations = [],
    submittedIds = [],
    onOpenChange,
    onSubmitSuccess,
    SubscribeForm,
  } = props;

  // body scroll prevention for iOS Safari
  useEffect(() => {
    if (!isOpen) return;

    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;
    const originalPosition = originalStyle.position;
    const originalHeight = originalStyle.height;
    const originalTouchAction = originalStyle.touchAction;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.height = '100%';
    document.body.style.width = '100%';
    document.body.style.touchAction = 'none';

    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.height = originalHeight;
      document.body.style.touchAction = originalTouchAction;
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  const renderedElement = (
    <div className={classNames('flex flex-col h-full')} data-recommendations-modal>
      <div className="flex justify-between gap-2 items-start p-6 pb-4 flex-shrink-0">
        <div className="flex flex-col gap-1">
          <h1
            className="text-[--site-theme-text-on-background-color] text-xl font-semibold"
            style={{ fontFamily: 'var(--site-theme-header-font)' }}
          >
            {email ? 'Thank you for subscribing!' : 'Recommended for you'}
          </h1>
          <h2
            className="text-[--site-theme-text-on-background-color] text-sm font-normal opacity-60"
            style={{ fontFamily: 'var(--site-theme-body-font)' }}
          >
            {email ? 'We also recommend these newsletters' : 'We think you’ll like these other newsletters'}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="text-[--site-theme-text-on-background-color] p-2 -mr-2 -mt-2 opacity-60 hover:opacity-80 flex-shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6" style={{ WebkitOverflowScrolling: 'touch' }}>
        {isLoading ? (
          <div className="flex flex-col gap-4 pb-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <RecommendationRowSkeleton key={index} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4 pb-4">
            {recommendations.map((recommendation) => (
              <RecommendationRow
                key={recommendation.id}
                email={email}
                recommendation={recommendation}
                submittedIds={submittedIds}
                onSubmitSuccess={onSubmitSuccess}
                SubscribeForm={SubscribeForm}
              />
            ))}
          </div>
        )}
      </div>

      <div
        className={classNames(
          'flex-shrink-0 border-t border-[--site-theme-border-color] bg-[--site-theme-background-color] p-6',
          'flex flex-col gap-2',
        )}
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}
      >
        <SubscribeForm recommendations={recommendations} onSubmitSuccess={() => onSubmitSuccess?.(recommendations)}>
          {({ isLoading: formLoading, email }) => (
            <SubscribeAllButton isLoading={formLoading} isDisabled={isLoading} email={email} />
          )}
        </SubscribeForm>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          disabled={isLoading}
          className={classNames(
            'px-4 py-1 rounded-[--site-theme-border-radius] bg-transparent text-[--site-theme-text-on-background-color] font-medium justify-center items-center duration-300',
            isLoading ? 'opacity-30 cursor-not-allowed' : 'opacity-50 hover:opacity-80',
          )}
          style={{ fontFamily: 'var(--site-theme-button-font)' }}
        >
          {submittedIds.length > 0 ? 'Next' : 'Skip'}
        </button>
      </div>
    </div>
  );

  const modalStyles = {
    '--overlay-background-color': '#00000050',
    '--modal-close-button-color': 'inherit',
    '--modal-close-button-placement-top': '24px',
    '--modal-close-button-placement-right': '24px',
  } as CSSProperties;

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onOpenChange}
      style={modalStyles}
      showRoundedBorders={false}
      className="shadow-lg rounded-[--site-theme-border-radius]"
      mobilePlacement="bottom"
      showCloseButton={false}
      overlayClassName="!z-[999]"
      shouldDisableScroll={false} // We handle scroll prevention manually for better iOS Safari support
    >
      <div
        className={classNames(modalDialogContent, modalContentContainer)}
        style={
          {
            ...(theme ? { ...webThemeToCSSVariables(theme) } : {}),
            '--width': '640px',
            '--maxWidth': '640px',
            height: 'min(90dvh, 800px)',
            maxHeight: '90dvh',
          } as CSSProperties
        }
      >
        {renderedElement}
      </div>
    </SimpleModal>
  );
}
